import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ConfirmAllotDialog, { isAlreadyAllotted } from "./ConfirmAllotDialog";

const API = process.env.REACT_APP_BACKEND_URL;

const getError = (error, fallback) =>
  error?.response?.data?.Exception ||
  error?.response?.data?.message ||
  fallback;

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMonth = (month) => {
  if (!month) return "-";

  const [year, monthNumber] = month.split("-").map(Number);
  const date = new Date(year, monthNumber - 1, 1);

  return date.toLocaleString("en-IN", {
    month: "short",
    year: "numeric",
  });
};

const AllocationList = ({ status, title, description, icon, basePath }) => {
  const navigate = useNavigate();
  const [allottingId, setAllottingId] = useState(null);
  const [mergeConfirm, setMergeConfirm] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const storedUser = JSON.parse(localStorage.getItem("user"));

  const fetchAllocations = async () => {
    try {
      setLoading(true);
      setError("");

      const endpoint =
        status === "DRAFT"
          ? `${API}/production-allocation/reserves`
          : `${API}/production-allocation/allotments`;
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${storedUser?.accessToken}`,
        },
      });

      setAllocations(response.data || []);
    } catch (err) {
      setAllocations([]);

      setError(
        getError(
          err,
          `Unable to load ${status === "DRAFT" ? "reserves" : "allotments"}.`,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllocations();
  }, [status]);

  const handleAllot = async (allocationId, confirmMerge = false) => {
    try {
      setAllottingId(allocationId);
      setError("");

      await axios.post(
        `${API}/production-allocation/${allocationId}/allot${
          confirmMerge ? "?confirmMerge=true" : ""
        }`,
        {},
        {
          headers: {
            Authorization: `Bearer ${storedUser?.accessToken}`,
          },
        },
      );

      setMergeConfirm(null);
      await fetchAllocations();
    } catch (err) {
      if (!confirmMerge && isAlreadyAllotted(err)) {
        setMergeConfirm({
          allocationId,
          message: err.response.data.Exception,
        });
      } else {
        setMergeConfirm(null);
        setError(getError(err, "Unable to allot the reserve."));
      }
    } finally {
      setAllottingId(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        px: { xs: 1.5, sm: 2.5, md: 3 },
        py: { xs: 2, md: 3 },
      }}
    >
      <ConfirmAllotDialog
        open={Boolean(mergeConfirm)}
        message={mergeConfirm?.message}
        saving={Boolean(allottingId)}
        onCancel={() => setMergeConfirm(null)}
        onConfirm={() => handleAllot(mergeConfirm.allocationId, true)}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          gap: 2,
          mb: 2.5,
          flexDirection: {
            xs: "column",
            md: "row",
          },
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {icon}

            <Typography
              sx={{
                fontSize: {
                  xs: 24,
                  md: 28,
                },
                fontWeight: 700,
                color: "#172033",
              }}
            >
              {title}
            </Typography>

            <Chip
              label={status === "DRAFT" ? "Draft" : "Final"}
              size="small"
              sx={{
                height: 23,
                backgroundColor: status === "DRAFT" ? "#fffaeb" : "#ecfdf3",
                color: status === "DRAFT" ? "#b54708" : "#027a48",
                fontWeight: 600,
                fontSize: 10,
              }}
            />
          </Box>

          <Typography
            sx={{
              mt: 0.55,
              fontSize: 13,
              color: "#667085",
            }}
          >
            {description}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={15} /> : <RefreshIcon />}
          onClick={fetchAllocations}
          disabled={loading}
          sx={{
            height: 38,
            borderRadius: 1.5,
            px: 1.75,
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#d0d5dd",
            color: "#344054",
            backgroundColor: "#fff",
            "&:hover": {
              backgroundColor: "#f9fafb",
            },
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e4e7ec",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            px: {
              xs: 1.75,
              md: 2.25,
            },
            py: 1.75,
            borderBottom: "1px solid #eaecf0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: "#344054",
            }}
          >
            {status === "DRAFT" ? "Reserved allocations" : "Final allotments"}
          </Typography>

          <Chip
            label={`${allocations.length} record${
              allocations.length === 1 ? "" : "s"
            }`}
            size="small"
            sx={{
              height: 23,
              backgroundColor: "#f2f4f7",
              color: "#475467",
              fontSize: 10,
              fontWeight: 600,
            }}
          />
        </Box>

        {loading ? (
          <Box
            sx={{
              py: 8,
              textAlign: "center",
            }}
          >
            <CircularProgress size={28} />

            <Typography
              sx={{
                mt: 1.5,
                fontSize: 13,
                color: "#667085",
              }}
            >
              Loading {status === "DRAFT" ? "reserves" : "allotments"}
              ...
            </Typography>
          </Box>
        ) : !allocations.length ? (
          <Box
            sx={{
              py: 8,
              textAlign: "center",
              px: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#475467",
              }}
            >
              No {status === "DRAFT" ? "reserves" : "allotments"} found
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: 13,
                color: "#98a2b3",
              }}
            >
              {status === "DRAFT"
                ? "Saved reserve drafts will appear here."
                : "Finalized production allotments will appear here."}
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: "calc(100vh - 260px)",
            }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 950 }}>
              <TableHead>
                <TableRow>
                  <HeaderCell>Allocation ID</HeaderCell>
                  <HeaderCell>Month</HeaderCell>
                  <HeaderCell>Report</HeaderCell>
                  <HeaderCell align="right">Items</HeaderCell>
                  <HeaderCell align="right">Customers</HeaderCell>
                  <HeaderCell align="right">Quantity</HeaderCell>
                  <HeaderCell>Created By</HeaderCell>
                  <HeaderCell>Date</HeaderCell>
                  <HeaderCell align="center">Open</HeaderCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {allocations.map((allocation) => (
                  <TableRow
                    key={allocation.allocationId}
                    hover
                    onClick={() =>
                      navigate(`${basePath}/${allocation.allocationId}`)
                    }
                    sx={{
                      cursor: "pointer",
                      "&:last-child td": {
                        borderBottom: 0,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#175cd3",
                          fontFamily: "IBM Plex Mono, monospace",
                        }}
                      >
                        {allocation.allocationId}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Cell value={formatMonth(allocation.month)} strong />
                    </TableCell>

                    <TableCell sx={{ maxWidth: 180 }}>
                      <Cell value={allocation.reportId || "-"} mono />
                    </TableCell>

                    <TableCell align="right">
                      <Cell value={formatNumber(allocation.itemCount)} />
                    </TableCell>

                    <TableCell align="right">
                      <Cell value={formatNumber(allocation.customerCount)} />
                    </TableCell>

                    <TableCell align="right">
                      <Cell
                        value={formatNumber(allocation.totalAllocatedQty)}
                        strong
                      />
                    </TableCell>

                    <TableCell>
                      <Cell value={allocation.createdBy || "-"} />
                    </TableCell>

                    <TableCell>
                      <Cell value={formatDate(allocation.createdDate)} />
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        {status === "DRAFT" && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleAllot(allocation.allocationId);
                            }}
                            disabled={allottingId === allocation.allocationId}
                            sx={{
                              minWidth: 60,
                              textTransform: "none",
                              fontSize: 11,
                              fontWeight: 600,
                              borderRadius: 1.25,
                              borderColor: "#86efac",
                              color: "#15803d",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {allottingId === allocation.allocationId
                              ? "..."
                              : "Allot"}
                          </Button>
                        )}

                        <Button
                          size="small"
                          endIcon={
                            <ArrowForwardIosRoundedIcon sx={{ fontSize: 12 }} />
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`${basePath}/${allocation.allocationId}`);
                          }}
                          sx={{
                            textTransform: "none",
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#175cd3",
                            whiteSpace: "nowrap",
                          }}
                        >
                          View
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

const HeaderCell = ({ children, align = "left" }) => (
  <TableCell
    align={align}
    sx={{
      backgroundColor: "#f8fafc",
      color: "#667085",
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.03em",
      whiteSpace: "nowrap",
      borderBottom: "1px solid #eaecf0",
    }}
  >
    {children}
  </TableCell>
);

const Cell = ({ value, strong, mono }) => (
  <Typography
    title={value}
    sx={{
      fontSize: 12,
      fontWeight: strong ? 600 : 400,
      color: "#344054",
      fontFamily: mono ? "IBM Plex Mono, monospace" : "inherit",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {value}
  </Typography>
);

export default AllocationList;
